import moment from 'moment-timezone';
import { format } from 'date-fns';
import { TimeslotFrequencyInput } from 'globalTypes';

export type RecurringFrequencyInput = Exclude<TimeslotFrequencyInput, 'once'>;

export default function dateTimeFormat(dateString: Date, options?: any): string {
  return new global.Intl.DateTimeFormat('en-us', options).format(dateString);
}

// returns a string formatted like this: Thu, Dec 17th at 12:00 PM
export function userReadableDateTimeFormat(date: Date | string) {
  return format(date, 'ddd, MMM Do [at] h:mm A');
}

export function mapRecurringFrequencyToInterval(frequency: RecurringFrequencyInput) {
  if (frequency === 'weekly') return 1;
  else if (frequency === 'everyOtherWeek') return 2;
  return undefined;
}

export function formatRecurrenceRuleDate(date: Date): string {
  return moment.utc(date).format('YYYYMMDDTHHmmss[Z]');
}

export function convertRecurrenceRuleDateToUserReadableString(date: string): string {
  return userReadableDateTimeFormat(moment.utc(date, 'YYYYMMDDTHHmmss[Z]').toDate());
}

// takes a list of recurrence rules, finds the one with exception dates,
// and converts them to an array of user readable strings
export function extractExceptionDatesToUserReadableStrings(recurrenceRules: string[]): string[] {
  const exceptionDateString = recurrenceRules.find(rule => rule.includes('EXDATE'));
  if (!exceptionDateString) return [];

  // EXDATE:20201224T000000Z,20201224T000000Z -> ["EXDATE","20201224T000000Z,20201224T000000Z"]
  const [_, datesString] = exceptionDateString.split(':');
  // "20201224T000000Z,20201224T000000Z" -> ["20201224T000000Z","20201224T000000Z"]
  const dates = datesString.split(',');
  return dates.map(d => convertRecurrenceRuleDateToUserReadableString(d));
}

export function formatWeeklyRecurrenceRule(interval: number, until?: Date | string) {
  const weeklyRecurrenceRuleSet = ['RRULE:FREQ=WEEKLY'];
  weeklyRecurrenceRuleSet.push(`INTERVAL=${interval}`);
  if (until) {
    weeklyRecurrenceRuleSet.push(`UNTIL=${formatRecurrenceRuleDate(new Date(until))}`);
  }
  return weeklyRecurrenceRuleSet.join(';');
}

export function formatExceptionDateRecurrenceRule(exceptionDates: (Date | string)[]) {
  if (exceptionDates.length === 0) return '';
  return `EXDATE:${exceptionDates.map(date => formatRecurrenceRuleDate(new Date(date))).join(',')}`;
}

export function formatRecurrenceRules(params: {
  interval: number;
  until?: Date | string;
  exceptionDates?: (Date | string)[];
  excludeUnavailableDates: boolean;
}) {
  const weeklyRecurrenceRule = formatWeeklyRecurrenceRule(params.interval, params.until);

  if (params.excludeUnavailableDates && params.exceptionDates && params.exceptionDates.length > 0) {
    const exceptionDateRecurrenceRule = formatExceptionDateRecurrenceRule(params.exceptionDates);
    return [weeklyRecurrenceRule, exceptionDateRecurrenceRule];
  }
  return [weeklyRecurrenceRule];
}
